import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import './AccessRecruitProfessional.css';
import { styles } from './AccessRecruitPageStyles';

import {
  MdRocketLaunch,
  MdAutoGraph,
  MdAnalytics,
  MdGroups,
  MdPerson,
  MdTimerOff,
  MdClose,
  MdCheck,
  MdWarning,
  MdError,
  MdLock,
  MdEmail,
  MdPersonOutline,
  MdBusiness,
  MdPeople,
  MdDescription,
  MdSend,
  MdArrowBack,
  MdUpgrade
} from "react-icons/md";

const AccessRecruitPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [trialEnded, setTrialEnded] = useState(false);
  const [hasCheckedTrial, setHasCheckedTrial] = useState(false);
  const [canResendOTP, setCanResendOTP] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    company: '',
    employees: '1-50',
    hiringNeeds: ''
  });

  const timerRef = useRef(null);
  const lastOtpSentTimeRef = useRef(null);
  const timerStartTimeRef = useRef(null);

  useEffect(() => {
    console.log('Current URL path:', location.pathname);
    console.log('Current URL search:', location.search);

    if (location.pathname === '/signup') {
      console.log('Setting form to SIGNUP mode (from /signup URL)');
      setIsLogin(false);
    } else if (location.pathname === '/login') {
      console.log('Setting form to LOGIN mode (from /login URL)');
      setIsLogin(true);
    }
    else if (location.pathname === '/access-recruit') {
      const searchParams = new URLSearchParams(location.search);
      const mode = searchParams.get('mode');

      if (mode === 'signup') {
        console.log('Setting form to SIGNUP mode (from query param)');
        setIsLogin(false);
      } else if (mode === 'login') {
        console.log('Setting form to LOGIN mode (from query param)');
        setIsLogin(true);
      }
    }
  }, [location]);

  const startResendTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setCanResendOTP(false);
    setResendTimer(60);
    lastOtpSentTimeRef.current = Date.now();
    timerStartTimeRef.current = performance.now();

    console.log('⏱️ Timer started at:', {
      timestamp: Date.now(),
      readable: new Date().toLocaleTimeString(),
      timerStart: timerStartTimeRef.current
    });

    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((performance.now() - timerStartTimeRef.current) / 1000);
      const remaining = Math.max(0, 60 - elapsed);

      setResendTimer(remaining);

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCanResendOTP(true);
        console.log('⏱️ Timer completed');
      }
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const checkUserStatus = async () => {
      const token = sessionStorage.getItem('authToken');
      const userEmail = sessionStorage.getItem('userEmail');
      const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');

      console.log('🔍 Checking user status:', {
        hasToken: !!token,
        hasEmail: !!userEmail,
        paymentStatus: userData.subscription?.paymentStatus,
        subscriptionEndDate: userData.subscription?.subscriptionEndDate,
        userData: userData
      });

      if (userData.subscription?.paymentStatus === 'active' && userData.subscription?.plan === 'paid') {
        console.log('✅ Paid user detected from session, redirecting to dashboard');
        navigate('/hiring-dashboard');
        return;
      }

      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/hiring/subscription-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const {
            isTrialActive,
            remainingTrialDays,
            paymentStatus,
            isSubscriptionActive,
            subscriptionEndDate,
            remainingSubscriptionDays,
            hasActiveAccess
          } = response.data.data;

          console.log('📊 User status from API:', {
            isTrialActive,
            remainingTrialDays,
            paymentStatus,
            isSubscriptionActive,
            subscriptionEndDate,
            remainingSubscriptionDays,
            hasActiveAccess
          });

          if (hasActiveAccess) {
            console.log('✅ User has active access, redirecting to dashboard');
            navigate('/hiring-dashboard');
            return;
          }

          if (!hasActiveAccess) {
            console.log('❌ No active access - trial ended or subscription expired');
            sessionStorage.clear();
            setTrialEnded(true);
            setHasCheckedTrial(true);
            return;
          }

          setHasCheckedTrial(true);
        } catch (error) {
          console.error('Trial check error:', error);

          if (error.response?.status === 401 || error.message?.includes('jwt malformed')) {
            console.log('🔄 Token invalid/malformed, clearing session and showing login');
            sessionStorage.clear();
            setHasCheckedTrial(true);
            return;
          }

          if (error.response?.data?.code === 'TRIAL_ENDED' || error.response?.data?.code === 'ACCESS_ENDED') {
            console.log('🔄 Trial ended, redirecting to pricing page');
            sessionStorage.clear();

            const userEmail = error.response?.data?.userEmail || sessionStorage.getItem('userEmail');

            navigate('/pricing', {
              state: {
                fromTrialEnded: true,
                userEmail: userEmail
              }
            });
            return;
          }

          setHasCheckedTrial(true);
        }
      } else {
        setHasCheckedTrial(true);
      }
    };

    checkUserStatus();
  }, [navigate]);

  useEffect(() => {
    if (otpSent) {
      const lastOtpTime = localStorage.getItem('lastOtpSentTime');
      if (lastOtpTime) {
        const timeElapsed = Date.now() - parseInt(lastOtpTime);
        const timeRemaining = Math.max(0, 60 - Math.floor(timeElapsed / 1000));

        if (timeRemaining > 0) {
          setCanResendOTP(false);
          setResendTimer(timeRemaining);
          startResendTimerFrom(timeRemaining);
        } else {
          setCanResendOTP(true);
        }
      }
    }
  }, [otpSent]);

  const startResendTimerFrom = (seconds) => {
    setResendTimer(seconds);

    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResendOTP(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    clearMessages();

    const targetEmail = isLogin ? email : signupData.email;

    if (!targetEmail) {
      setError('Please enter your email');
      return;
    }

    if (!isLogin && (!signupData.name || !signupData.company || !signupData.hiringNeeds)) {
      setError('Please fill all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(targetEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/hiring/send-otp`, {
        email: targetEmail,
        purpose: isLogin ? 'login' : 'signup'
      });

      if (response.data.success) {
        setOtpSent(true);
        setSuccess(`Verification code sent to ${targetEmail}`);
        setError('');

        startResendTimer();
        localStorage.setItem('lastOtpSentTime', Date.now().toString());
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);

      if (error.response?.data?.code === 'TRIAL_ENDED' || error.response?.data?.code === 'ACCESS_ENDED') {
        const userEmail = error.response?.data?.userEmail || targetEmail;

        sessionStorage.setItem('trialEndedEmail', userEmail);
        navigate('/pricing', {
          state: {
            fromTrialEnded: true,
            userEmail: userEmail
          }
        });
        return;
      }

      const errorMessage = error.response?.data?.message ||
        'Failed to send OTP. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResendOTP) return;

    clearMessages();

    const targetEmail = isLogin ? email : signupData.email;

    if (!targetEmail) {
      setError('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/hiring/resend-otp`, {
        email: targetEmail,
        purpose: isLogin ? 'login' : 'signup'
      });

      if (response.data.success) {
        setSuccess(`New verification code sent to ${targetEmail}`);
        setError('');

        startResendTimer();
        localStorage.setItem('lastOtpSentTime', Date.now().toString());
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      const errorMessage = error.response?.data?.message ||
        'Failed to resend OTP. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    const targetEmail = isLogin ? email : signupData.email;

    setIsSubmitting(true);
    try {
      const requestData = {
        email: targetEmail,
        otp: otp
      };

      if (!isLogin) {
        requestData.userData = {
          name: signupData.name,
          email: signupData.email,
          company: signupData.company,
          employees: signupData.employees,
          hiringNeeds: signupData.hiringNeeds,
          userType: 'hr_manager'
        };
      }

      const response = await axios.post(`${API_BASE_URL}/api/hiring/verify-otp`, requestData);

      if (response.data.success) {
        setSuccess(response.data.message);
        setError('');

        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        localStorage.removeItem('lastOtpSentTime');

        const token = response.data.token;
        const userData = response.data.data;

        console.log('✅ User logged in successfully:', {
          token: token ? 'Yes' : 'No',
          userData: userData,
          subscription: userData.subscription
        });

        sessionStorage.setItem('authToken', token);
        sessionStorage.setItem('userData', JSON.stringify(userData));
        sessionStorage.setItem('userEmail', targetEmail);
        sessionStorage.setItem('userId', userData._id || userData.userId || '');
        sessionStorage.setItem('userName', userData.name || '');
        sessionStorage.setItem('companyName', userData.company || '');
        sessionStorage.setItem('userType', userData.userType || 'hr_manager');

        console.log('🔐 Session storage updated:', {
          tokenStored: !!sessionStorage.getItem('authToken'),
          userDataStored: !!sessionStorage.getItem('userData'),
          userEmailStored: !!sessionStorage.getItem('userEmail'),
          userType: sessionStorage.getItem('userType')
        });

        const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');
        const selectedPlanAfterLogin = sessionStorage.getItem('selectedPlanAfterLogin');

        console.log('🔍 DEBUG - After successful login:');
        console.log('redirectAfterLogin:', redirectAfterLogin);
        console.log('selectedPlanAfterLogin:', selectedPlanAfterLogin);

        if (redirectAfterLogin === '/payment' && selectedPlanAfterLogin) {
          try {
            const plan = JSON.parse(selectedPlanAfterLogin);

            sessionStorage.removeItem('redirectAfterLogin');
            sessionStorage.removeItem('selectedPlanAfterLogin');

            console.log('🚀 Redirecting to payment with plan:', plan);

            navigate('/payment', { state: { plan } });

          } catch (parseError) {
            console.error('Error parsing selected plan:', parseError);
            navigate('/hiring-dashboard');
          }
        } else {
          console.log('📊 Redirecting to dashboard');
          navigate('/hiring-dashboard');
        }

      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);

      if (error.response?.data?.code === 'TRIAL_ENDED' || error.response?.data?.code === 'ACCESS_ENDED') {
        const userEmail = error.response?.data?.userEmail || targetEmail;

        console.log('🔄 Trial ended during OTP verification, redirecting to pricing');

        sessionStorage.setItem('trialEndedEmail', userEmail);
        navigate('/pricing', {
          state: {
            fromTrialEnded: true,
            userEmail: userEmail
          }
        });
        return;
      } else {
        const errorMessage = error.response?.data?.message ||
          'Verification failed. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp('');
    clearMessages();

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    localStorage.removeItem('lastOtpSentTime');
    setCanResendOTP(true);
    setResendTimer(0);
  };

  const handleTabSwitch = (login) => {
    setIsLogin(login);
    setOtpSent(false);
    setOtp('');
    clearMessages();
    setEmail('');
    setTrialEnded(false);
    setCanResendOTP(true);
    setResendTimer(0);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    localStorage.removeItem('lastOtpSentTime');

    if (!login) {
      setSignupData({
        name: '',
        email: '',
        company: '',
        employees: '1-50',
        hiringNeeds: ''
      });
    }

    if (login) {
      navigate('/login', { replace: true });
    } else {
      navigate('/signup', { replace: true });
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    clearMessages();
  };

  const handleGoToPricing = () => {
    const targetEmail = isLogin ? email : signupData.email;

    console.log('📍 DEBUG - handleGoToPricing called');
    console.log('isLogin:', isLogin);
    console.log('login email:', email);
    console.log('signup email:', signupData.email);
    console.log('targetEmail for pricing:', targetEmail);

    if (targetEmail) {
      sessionStorage.setItem('trialEndedEmail', targetEmail);
    }

    navigate('/pricing', {
      state: {
        fromTrialEnded: true,
        userEmail: targetEmail
      }
    });
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (!hasCheckedTrial) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <p>Checking your account status...</p>
        </div>
      </div>
    );
  }

  if (trialEnded) {
    return (
      <div style={styles.trialEndedContainer}>
        <div style={styles.trialEndedContent}>
          <div style={styles.trialEndedIcon}><MdTimerOff size={48} /></div>
          <h2 style={styles.trialEndedTitle}>Trial Period Ended</h2>
          <p style={styles.trialEndedMessage}>
            Your free trial has ended. Please upgrade to continue using our services.
          </p>
          <button
            onClick={handleGoToPricing}
            style={styles.upgradeButton}
          >
            <MdUpgrade size={20} /> Upgrade Now
          </button>
          <button
            onClick={() => {
              setTrialEnded(false);
              clearMessages();
            }}
            style={styles.backButton}
          >
            <MdArrowBack size={20} /> Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hrcp-auth-container">
      <div
        className={`hrcp-auth-content-container ${isLogin ? "layout-login" : "layout-signup"
          }`}
      >
        {/* Left Section - Login Form Card */}
        <div className="hrcp-auth-left">
          <div className="hrcp-auth-form-wrapper">
            {/* Logo Section - Inside card, only for initial form (before OTP) */}
            {!otpSent && (
              <div className="hrcp-logo-container">
                <div className="quantum-nav__logo">
                  <div className="quantum-nav__logo-orb">
                    <img 
                      src="/images/logo415.png" 
                      alt="Logo" 
                      className="quantum-nav__logo-img" 
                    />
                    <span className="Humanex_logo_title">umanex</span>
                  </div>
                </div>
              </div>
            )}
            
            {error && (error.includes('trial ended') || error.includes('access has ended')) && (
              <div className="hrcp-trial-ended-banner hrcp-alert hrcp-alert-warning">
                <div className="hrcp-banner-icon"><MdUpgrade size={24} /></div>
                <div className="hrcp-banner-content">
                  <h4>Upgrade Required</h4>
                  <p>Your free trial has ended. Upgrade to continue using our services.</p>
                </div>
              </div>
            )}

            {!otpSent ? (
              <div className={`form-content-wrapper ${!isLogin ? 'scrollable-content' : ''}`}>
                <div className="hrcp-auth-form-header">
                  <h1 className="hrcp-form-title">
                    {isLogin ? 'Welcome back' : 'Create an account'}
                  </h1>
                  <p className="hrcp-form-subtitle">
                    {isLogin ? 'Sign in to continue your journey' : 'Start your 30-day free trial'}
                  </p>
                </div>

                <div className="hrcp-auth-toggle">
                  <button
                    className={`hrcp-toggle-btn ${isLogin ? 'hrcp-active' : ''}`}
                    onClick={() => handleTabSwitch(true)}
                    type="button"
                  >
                    Login
                  </button>
                  <button
                    className={`hrcp-toggle-btn ${!isLogin ? 'hrcp-active' : ''}`}
                    onClick={() => handleTabSwitch(false)}
                    type="button"
                  >
                    Sign Up
                  </button>
                </div>

                {error && !error.includes('trial ended') && !error.includes('access has ended') && (
                  <div className={`hrcp-alert ${error.includes('Trial period') ? 'hrcp-alert-warning' : 'hrcp-alert-error'}`}>
                    <span className="hrcp-alert-icon">
                      {error.includes('Trial period') ? <MdWarning size={20} /> : <MdError size={20} />}
                    </span>
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="hrcp-alert hrcp-alert-success">
                    <span className="hrcp-alert-icon"><MdCheck size={20} /></span>
                    <span>{success}</span>
                  </div>
                )}

                <form className="hrcp-auth-form" onSubmit={handleSendOTP}>
                  {isLogin ? (
                    <>
                      <div className="hrcp-form-group">
                        <label className="hrcp-form-label">
                          <MdEmail size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Email Address
                        </label>
                        <input
                          type="email"
                          className="hrcp-form-input"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            clearMessages();
                          }}
                          placeholder="name@company.com"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="form-buttons-container">
                        <button
                          type="submit"
                          className="hrcp-btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <span className="hrcp-spinner"></span> : (
                            <>
                              <MdSend size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Continue
                            </>
                          )}
                        </button>

                        {(error && (error.includes('trial ended') || error.includes('access has ended'))) && (
                          <button
                            type="button"
                            className="hrcp-btn-pricing"
                            onClick={handleGoToPricing}
                          >
                            <MdUpgrade size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Upgrade Now
                          </button>
                        )}

                        <button
                          type="button"
                          className="hrcp-btn-text"
                          onClick={() => handleTabSwitch(false)}
                        >
                          Don't have an account? Sign up for free trial
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-scroll-area">
                        <div className="hrcp-form-row">
                          <div className="hrcp-form-group">
                            <label className="hrcp-form-label">
                              <MdPersonOutline size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Full Name
                            </label>
                            <input
                              type="text"
                              className="hrcp-form-input"
                              value={signupData.name}
                              onChange={(e) => {
                                setSignupData({ ...signupData, name: e.target.value });
                                clearMessages();
                              }}
                              placeholder="John Doe"
                              required
                              disabled={isSubmitting}
                            />
                          </div>

                          <div className="hrcp-form-group">
                            <label className="hrcp-form-label">
                              <MdEmail size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Email Address
                            </label>
                            <input
                              type="email"
                              className="hrcp-form-input"
                              value={signupData.email}
                              onChange={(e) => {
                                setSignupData({ ...signupData, email: e.target.value });
                                clearMessages();
                              }}
                              placeholder="name@company.com"
                              required
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>

                        <div className="hrcp-form-group">
                          <label className="hrcp-form-label">
                            <MdBusiness size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Company Name
                          </label>
                          <input
                            type="text"
                            className="hrcp-form-input"
                            value={signupData.company}
                            onChange={(e) => {
                              setSignupData({ ...signupData, company: e.target.value });
                              clearMessages();
                            }}
                            placeholder="Your Company"
                            required
                            disabled={isSubmitting}
                          />
                        </div>

                        <div className="hrcp-form-group">
                          <label className="hrcp-form-label">
                            <MdPeople size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Company Size
                          </label>
                          <select
                            className="hrcp-form-input"
                            value={signupData.employees}
                            onChange={(e) => {
                              setSignupData({ ...signupData, employees: e.target.value });
                              clearMessages();
                            }}
                            required
                            disabled={isSubmitting}
                          >
                            <option value="1-50">1-50 employees</option>
                            <option value="51-200">51-200 employees</option>
                            <option value="201-500">201-500 employees</option>
                            <option value="501-1000">501-1000 employees</option>
                            <option value="1000+">1000+ employees</option>
                          </select>
                        </div>

                        <div className="hrcp-form-group">
                          <label className="hrcp-form-label">
                            <MdDescription size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Hiring Needs
                          </label>
                          <textarea
                            className="hrcp-form-textarea"
                            value={signupData.hiringNeeds}
                            onChange={(e) => {
                              setSignupData({ ...signupData, hiringNeeds: e.target.value });
                              clearMessages();
                            }}
                            placeholder="Tell us about your hiring requirements, positions you're looking to fill, and any specific needs..."
                            required
                            disabled={isSubmitting}
                            rows="4"
                          />
                        </div>
                      </div>

                      <div className="form-buttons-container">
                        <button
                          type="submit"
                          className="hrcp-btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <span className="hrcp-spinner"></span> : (
                            <>
                              <MdRocketLaunch size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Start Free Trial
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="hrcp-btn-text"
                          onClick={() => handleTabSwitch(true)}
                        >
                          Already have an account? Log in
                        </button>
                      </div>
                    </>
                  )}
                </form>
              </div>
            ) : (
              <div className="form-content-wrapper">
                <div className="hrcp-otp-header">
                  <h3>Check your email</h3>
                  <p>
                    We sent a verification code to{' '}
                    <span className="hrcp-otp-email">
                      {isLogin ? email : signupData.email}
                    </span>
                  </p>

                  <div className="hrcp-resend-otp">
                    <p className="hrcp-resend-text">
                      Didn't receive the code?
                      {canResendOTP ? (
                        <button
                          type="button"
                          className="hrcp-resend-link"
                          onClick={handleResendOTP}
                          disabled={isSubmitting || !canResendOTP}
                        >
                          Resend OTP
                        </button>
                      ) : (
                        <span className="hrcp-resend-timer">
                          Resend available in {formatTimer(resendTimer)}
                        </span>
                      )}
                    </p>
                  </div>

                  {!isLogin && (
                    <div className="hrcp-trial-badge">
                      <span className="hrcp-badge-icon"><MdRocketLaunch size={18} /></span>
                      <span>30-Day Free Trial</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className={`hrcp-alert ${error.includes('trial ended') || error.includes('Trial period') || error.includes('access has ended') ? 'hrcp-alert-warning' : 'hrcp-alert-error'}`}>
                    <span className="hrcp-alert-icon">
                      {error.includes('trial ended') || error.includes('Trial period') || error.includes('access has ended') ? <MdWarning size={20} /> : <MdError size={20} />}
                    </span>
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="hrcp-alert hrcp-alert-success">
                    <span className="hrcp-alert-icon"><MdCheck size={20} /></span>
                    <span>{success}</span>
                  </div>
                )}

                <form className="hrcp-auth-form" onSubmit={handleVerifyOTP}>
                  <div className="hrcp-form-group">
                    <label className="hrcp-form-label">
                      <MdLock size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Verification Code
                    </label>
                    <input
                      type="text"
                      className="hrcp-form-input hrcp-otp-input"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-buttons-container">
                    <button
                      type="submit"
                      className="hrcp-btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <span className="hrcp-spinner"></span> : (
                        <>
                          <MdCheck size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Verify & Continue
                        </>
                      )}
                    </button>

                    {(error && (error.includes('trial ended') || error.includes('access has ended'))) && (
                      <button
                        type="button"
                        className="hrcp-btn-pricing"
                        onClick={handleGoToPricing}
                      >
                        <MdUpgrade size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Upgrade Now
                      </button>
                    )}

                    <button
                      type="button"
                      className="hrcp-btn-secondary"
                      onClick={handleBack}
                    >
                      <MdArrowBack size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Use different email
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessRecruitPage;